// Plan 06-05 Task 3 — CofreRequestCard.
//
// Card por request: state badge + countdown + ações condicionais por role
// e estado. Renderiza apenas metadados públicos (requestId trunc + denunciaId
// + motivoHash) — NUNCA expõe motivoCipher cleartext.

import { useTranslation } from 'react-i18next';
import { Button } from '@aions/ui-primitives';

export type CofreStateKind = 'Solicitado' | 'AssinaturasReunidas' | 'Concedido' | 'Revogado24h';

export interface CofreRequest {
  requestId: string;
  denunciaId: string;
  motivoHash: string;
  expiresAt: number;
  stateKind: CofreStateKind;
  signerCount: number;
  solicitanteUlid: string;
  /** Quem pediu, por nome. `null` quando o id não resolve (conta removida do roster) —
   *  aí a tela cai para o ULID em vez de omitir a linha. */
  solicitanteEmail?: string | null;
  /** Quem JÁ assinou, por nome. Contador sem nomes num quórum diz quantos faltam e não
   *  quem — o mesmo defeito do painel de pendências do comitê. */
  assinaram?: ReadonlyArray<string>;
}

interface Props {
  request: CofreRequest;
  currentUserId: string;
  onSign?: () => void;
  onReconstruct?: () => void;
  onRevoke?: () => void;
}

function stateClassName(kind: CofreStateKind): string {
  switch (kind) {
    case 'Solicitado':
      return 'cofre-card state-solicitado';
    case 'AssinaturasReunidas':
      return 'cofre-card state-assinaturas';
    case 'Concedido':
      return 'cofre-card state-concedido';
    case 'Revogado24h':
      return 'cofre-card state-revogado';
  }
}

function stateI18nKey(kind: CofreStateKind): string {
  return `cofre.state.${kind.toLowerCase()}`;
}

export function CofreRequestCard({
  request,
  currentUserId,
  onSign,
  onReconstruct,
  onRevoke,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const remainingMs = request.expiresAt - Date.now();
  const remainingHours = Math.max(0, Math.floor(remainingMs / 3_600_000));
  const isSolicitante = request.solicitanteUlid === currentUserId;
  const isOpen = request.stateKind === 'Solicitado' || request.stateKind === 'AssinaturasReunidas';

  return (
    <article
      className={stateClassName(request.stateKind)}
      aria-label={`Pedido ${request.requestId}`}
    >
      <header>
        <strong>
          <code>{request.requestId.slice(0, 16)}…</code>
        </strong>{' '}
        — <span>{t(stateI18nKey(request.stateKind))}</span>
      </header>
      <div className="cofre-card-meta">
        {/* 🔴 QUEM PEDIU vem PRIMEIRO (operador, 2026-08-19). O card mostrava caso, hash do
            motivo e prazo — e não dizia de quem era o pedido. Assinar um break-glass sem
            saber quem o abriu esvazia o quórum: a assinatura passa a atestar um
            procedimento em vez de uma pessoa. Fica no topo do bloco porque é a primeira
            pergunta de quem vai decidir se assina. */}
        <span data-testid="cofre-solicitante">
          {t('cofre.card.solicitante')}{' '}
          <strong>{request.solicitanteEmail ?? request.solicitanteUlid}</strong>
        </span>
        <span>
          {t('cofre.card.caso')} <code>{request.denunciaId}</code>
        </span>
        <span>
          {t('cofre.card.motivo_hash')} <code>{request.motivoHash.slice(0, 12)}…</code>
        </span>
        {(request.assinaram?.length ?? 0) > 0 && (
          <span data-testid="cofre-assinaram">
            {t('cofre.card.assinaram')} <strong>{(request.assinaram ?? []).join(', ')}</strong>
          </span>
        )}
        <span>
          {t('cofre.card.expira_em')} {remainingHours}h
        </span>
        <span>
          {t('cofre.card.assinaturas')} {request.signerCount} / 2
        </span>
      </div>
      <div className="cofre-card-actions">
        {request.stateKind === 'Solicitado' && !isSolicitante && onSign && (
          <Button onClick={onSign}>{t('cofre.button.sign')}</Button>
        )}
        {request.stateKind === 'AssinaturasReunidas' && onReconstruct && (
          <Button onClick={onReconstruct}>{t('cofre.button.reconstruct')}</Button>
        )}
        {isOpen && onRevoke && (
          <Button variant="danger" onClick={onRevoke}>
            {t('cofre.button.revoke')}
          </Button>
        )}
      </div>
    </article>
  );
}
